import type { Cell } from '@tanstack/react-table';
import { type ForwardedRef, forwardRef, type HTMLProps } from 'react';

import { getClassNameOverride } from '~/chords/getClassNameOverride';
import { cn } from '~/chords/lib/cn';
import { variants } from '~/chords/lib/variants';

import type { ComponentProps } from '../../../ComponentProps';
import { useTableContext } from '../../hooks/useTableContext';

interface TableCellProps<TData extends { id: string }>
  extends ComponentProps<HTMLProps<HTMLTableCellElement>> {
  cell?: Cell<TData, unknown>;
  isInteractive?: boolean;
}

const tableCellVariants = variants({
  base: cn(
    'border-y-px',
    'border-transparent',
    'p-md',
    'first:rounded-s-sm',
    'first:border-r-0',
    'first:border-l-px',
    'last:rounded-r-sm',
    'last:border-l-0',
    'last:border-r-px',
    '[&:has([role=checkbox])]:pr-0',
    '[&>[role=checkbox]]:translate-y-[2px]',
  ),
  variants: {
    isInteractive: {
      true: cn(
        'group-hover:bg-emphasis-subtle',
        'group-hover:border-emphasis-stronger',
        'transition-colors',
        'duration-300',
        'ease-out',
      ),
    },
    size: {
      xs: cn('p-xs'),
      sm: cn('p-sm'),
      md: cn('p-md'),
      lg: cn('p-lg'),
      xl: cn('p-xl'),
    },
    align: {
      left: cn('text-left'),
      right: cn('text-right'),
    },
  },
});

function TableCellInner<TData extends { id: string }>(
  { cell, children, isInteractive = false, ...props }: TableCellProps<TData>,
  ref: ForwardedRef<HTMLTableCellElement>,
) {
  const { size } = useTableContext();

  const className = getClassNameOverride(props, 'TableCell');

  const cellAlign = cell?.column.columnDef.meta?.align ?? 'left';

  const style = cell
    ? {
        width: `calc(var(--header-${cell.column?.id}-size) * 1px)`,
      }
    : {};

  return (
    <td
      {...props}
      ref={ref}
      className={cn(
        tableCellVariants({
          isInteractive,
          size,
          align: cellAlign === 'right' ? 'right' : 'left',
        }),
        className,
      )}
      style={style}
    >
      {children}
    </td>
  );
}

TableCellInner.displayName = 'TableCell';

// @TODO RMW-113: Figure out how to type this properly without an assertion
// This works, but it's definitely not ideal, since it's rather confusing.
export const TableCell = forwardRef(TableCellInner) as <
  TData extends { id: string },
>(
  props: TableCellProps<TData> & {
    ref?: ForwardedRef<HTMLTableCellElement>;
  },
) => ReturnType<typeof TableCellInner>;
